import React from 'react';
import {
    Box,
    Grid,
    VStack,
    HStack,
    Text,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Icon,
} from '@chakra-ui/react';
import {
    FiUsers,
    FiPieChart,
    FiBarChart2,
    FiDollarSign,
    FiTrendingUp,
    FiTarget,
    FiCodesandbox,
    FiGift,
    FiCodesandbox as FiCodesandboxIcon,
    FiTarget as FiTargetIcon,
    FiPieChart as FiPieChartIcon,
    FiBarChart2 as FiBarChart2Icon,
} from 'react-icons/fi';
import { useAuction } from '../context/AuctionContext';
import SolanaLogo from './SolanaLogo';
import statsCalculator from '../utils/statsCalculator';
import { AUCTION_DAYS, SOL_PRICE_USD, TOTAL_AUCTION_TOKENS } from '../utils/constants';
import BigNumber from 'bignumber.js';
import priceCalculator from '../utils/priceCalculator';

// Phase constants
const PHASE_1_DAYS = 15;
const PHASE_2_DAYS = 30;
const PHASE_3_DAYS = 40;
const PHASE_1_BONUS = 5;
const PHASE_2_BONUS = 3;
const PHASE_3_BONUS = 1;
const PHASE_4_BONUS = 0.5;
const START_PRICE = 0.00001;

const StatBox = ({ icon, label, value, subValue, color, highlight }) => (
    <Stat
        px={4}
        py={3}
        bg={highlight ? `${color}1a` : "rgba(17, 25, 40, 0.75)"}
        borderRadius="xl"
        border="1px solid"
        borderColor={highlight ? color : "rgba(255, 255, 255, 0.125)"}
        backdropFilter="blur(16px) saturate(180%)"
        _hover={{ 
            transform: 'scale(1.02)',
            transition: 'transform 0.2s'
        }}
        position="relative"
        overflow="hidden"
        height="100%"
        _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '6px',
            height: '100%',
            bg: color,
            opacity: highlight ? 1 : 0,
        }}
    >
        <VStack spacing={1} align="stretch" height="100%" justify="center">
            <HStack spacing={2} align="center">
                <Icon as={icon} boxSize={5} color={color} />
                <Text color="whiteAlpha.600" fontSize="sm">{label}</Text>
            </HStack>
            <Text color="white" fontSize="xl" fontWeight="bold" lineHeight="1.2">
                {value}
            </Text>
            {subValue && (
                <Text fontSize="xs" color="whiteAlpha.600" mt="1">
                    {subValue}
                </Text>
            )}
        </VStack>
    </Stat>
);

const PhaseBadge = ({ phase, bonus, isActive }) => (
    <Box
        variant={isActive ? "solid" : "subtle"}
        colorScheme={
            phase === 1 ? "blue" :
            phase === 2 ? "purple" :
            phase === 3 ? "teal" :
            "indigo"
        }
        px={3}
        py={1}
        borderRadius="full"
        fontSize="sm"
    >
        Phase {phase}: {bonus}% Bonus
    </Box>
);

const AuctionDashboard = () => {
    const { 
        currentDay,
        auctionState,
        totalSolRaised,
        SOL_PRICE_USD
    } = useAuction();

    // Constants for simulated totals
    const TOTAL_SIMULATED_PARTICIPANTS = 4273;
    const TOTAL_SIMULATED_SOL = 4036;

    // Calculate total participants and SOL raised
    const calculateTotalParticipants = () => {
        // Check if the user has made any contributions
        const hasUserContributions = Object.values(auctionState?.userContributions || {}).some(dayContribs => dayContribs.length > 0);
        
        // Add 1 to the total participants count if the user has made any contributions
        return hasUserContributions ? TOTAL_SIMULATED_PARTICIPANTS + 1 : TOTAL_SIMULATED_PARTICIPANTS;
    };

    const totalParticipants = calculateTotalParticipants();
    console.log("AuctionDashboard totalParticipants:", {
        totalParticipants,
        hasUserContributions: Object.values(auctionState?.userContributions || {}).some(dayContribs => dayContribs.length > 0),
        userContributions: auctionState?.userContributions
    });
    const totalSOLRaised = totalSolRaised || TOTAL_SIMULATED_SOL;
    const stats = priceCalculator.calculate(totalSOLRaised);

    // Get current phase and bonus
    const getCurrentPhase = () => {
        const day = currentDay || 1;
        if (day <= PHASE_1_DAYS) return { phase: 1, bonus: PHASE_1_BONUS };
        if (day <= PHASE_2_DAYS) return { phase: 2, bonus: PHASE_2_BONUS };
        if (day <= PHASE_3_DAYS) return { phase: 3, bonus: PHASE_3_BONUS };
        return { phase: 4, bonus: PHASE_4_BONUS };
    };

    const { phase, bonus } = getCurrentPhase();

    const getProgressGradient = (percentage) => {
        if (percentage <= 10) {
            return 'linear-gradient(to right, #8B0000 0%, #FF0000 100%)';
        } else if (percentage <= 20) {
            return 'linear-gradient(to right, #8B0000 0%, #FF0000 50%, #FF4500 100%)';
        } else if (percentage <= 35) {
            return 'linear-gradient(to right, #8B0000 0%, #FF0000 33%, #FF4500 66%, #FFD700 100%)';
        } else if (percentage <= 55) {
            return 'linear-gradient(to right, #8B0000 0%, #FF0000 25%, #FF4500 50%, #FFD700 75%, #00FF00 100%)';
        } else if (percentage <= 75) {
            return 'linear-gradient(to right, #8B0000 0%, #FF0000 20%, #FF4500 40%, #FFD700 60%, #00FF00 80%, #00FFFF 100%)';
        } else if (percentage <= 90) {
            return 'linear-gradient(to right, #8B0000 0%, #FF0000 16%, #FF4500 32%, #FFD700 48%, #00FF00 64%, #00FFFF 80%, #0000FF 100%)';
        } else {
            return 'linear-gradient(to right, #8B0000 0%, #FF0000 14%, #FF4500 28%, #FFD700 42%, #00FF00 56%, #00FFFF 70%, #0000FF 85%, #8A2BE2 100%)';
        }
    };

    return (
        <Box
            width="100%"
            p={6}
            bg="rgba(16, 16, 16, 0.9)"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
            position="relative"
            overflow="hidden"
            boxShadow="0 0 40px rgba(153, 69, 255, 0.3)"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bg: "radial-gradient(circle at center, rgba(153, 69, 255, 0.15), transparent 70%)",
                pointerEvents: "none",
                zIndex: 0
            }}
        >
            <VStack spacing={6} align="stretch" width="100%" position="relative" zIndex={1}>
                <HStack justify="space-between" align="center">
                    <Text
                        fontSize="xl"
                        fontWeight="bold"
                        bgGradient="linear(to-r, #8247E5, #FF3366)"
                        bgClip="text"
                    >
                        Auction Progress
                    </Text>
                    <PhaseBadge phase={phase} bonus={bonus} isActive={true} />
                </HStack>

                {/* First row - 4 boxes */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4} width="100%">
                    <StatBox 
                        icon={FiUsers} 
                        label="Total Participants" 
                        value={priceCalculator.formatNumber(totalParticipants)}
                        subValue="Already reserved"
                        color="#FF3D00"
                        highlight={true}
                    />
                    <StatBox 
                        icon={SolanaLogo} 
                        label="Total SOL" 
                        value={priceCalculator.formatNumber(totalSOLRaised)}
                        subValue={`$${priceCalculator.formatNumber(stats.marketCap)}`}
                        color="#FF9100"
                        highlight={true}
                    />
                    <StatBox 
                        icon={FiPieChart} 
                        label="Total Supply" 
                        value={priceCalculator.formatNumber(stats.circulatingSupply)}
                        subValue="JINU Tokens"
                        color="#FFD600"
                        highlight={true}
                    />
                    <StatBox 
                        icon={FiBarChart2} 
                        label="Market Cap" 
                        value={priceCalculator.formatMarketCap(stats.marketCap)}
                        subValue="Based on Contributions"
                        color="#AEEA00"
                        highlight={true}
                    />
                </SimpleGrid>

                {/* Second row - 4 boxes */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
                    <StatBox 
                        icon={FiDollarSign} 
                        label="Start Price" 
                        value={priceCalculator.formatPrice(stats.startPrice)}
                        subValue="Initial Token Price"
                        color="#4CAF50"
                        highlight={true}
                    />
                    <StatBox 
                        icon={FiTrendingUp} 
                        label="Current Price" 
                        value={priceCalculator.formatPrice(stats.currentPrice)}
                        subValue="Based on Contributions"
                        color="#00B0FF"
                        highlight={true}
                    />
                    <StatBox 
                        icon={FiTarget} 
                        label="Projected DEX" 
                        value={priceCalculator.formatPrice(stats.dexPrice)}
                        subValue="Estimated Launch Price"
                        color="#0277BD"
                        highlight={true}
                    />
                    <StatBox 
                        icon={FiCodesandbox} 
                        label="Moonshot" 
                        value={priceCalculator.formatMoonshot(stats.moonshot)}
                        subValue="Price Multiplier"
                        color="#673AB7"
                        highlight={true}
                    />
                </SimpleGrid>

                {/* Progress Bar */}
                <Box>
                    <HStack justify="space-between" mb={2}>
                        <Text color="gray.400">Progress</Text>
                        <Text color="gray.400">{((currentDay || 0) / AUCTION_DAYS * 100).toFixed(0)}%</Text>
                    </HStack>
                    <Box
                        h="8px"
                        w="100%"
                        bg="whiteAlpha.100"
                        borderRadius="full"
                        overflow="hidden"
                        position="relative"
                    >
                        <Box
                            h="100%"
                            w={`${((currentDay || 0) / AUCTION_DAYS * 100)}%`}
                            bgImage={getProgressGradient(((currentDay || 0) / AUCTION_DAYS * 100))}
                            borderRadius="full"
                            transition="all 0.3s ease"
                        />
                    </Box>
                    <HStack justify="space-between" mt={2}>
                        <Text fontSize="sm" color="gray.500">Day {currentDay || 0}</Text>
                        <Text fontSize="sm" color="gray.500">Day {AUCTION_DAYS}</Text>
                    </HStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default AuctionDashboard;
